import { defaultSmartListColorOption } from '@griply/utils';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconFormNames } from 'shared/assets/icons';
import {
  Button,
  Colors,
  Sizes as ButtonSizes,
  Variants,
} from 'shared/components/ui/button';
import { ColorPickerFormSelect } from 'shared/components/ui/color-picker';
import { FormDialogHeader } from 'shared/components/ui/form-dialog-header';
import { FormIcon } from 'shared/components/ui/form-icon';
import { FormInput } from 'shared/components/ui/form-input';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { SmartListFormFields } from 'shared/types/smart-list-form';

import * as Styled from './smart-list-form.style';
import { useSmartListForm } from './use-smart-list-form';

export type SmartListFormProps = {
  initialValues?: SmartListFormFields;
  onSubmit: (data: SmartListFormFields) => void;
  onCancel: () => void;
  isEdit?: boolean;
  filterEditor?: React.ReactNode;
};

const defaultInitialValues: SmartListFormFields = {
  name: '',
  color: defaultSmartListColorOption.value,
  icon: IconFormNames.SmartList,
};

export const SmartListForm: React.FC<SmartListFormProps> = ({
  initialValues = {},
  onSubmit,
  onCancel,
  isEdit,
  filterEditor,
}) => {
  const { t } = useTranslation();
  const {
    register,
    onSubmit: submitForm,
    errors,
    isValid,
    setValue,
    watch,
  } = useSmartListForm(onSubmit, { ...defaultInitialValues, ...initialValues });

  const { ref: nameRefCallback, ...nameProps } = register('name');
  const nameRef = useForwardedRef(nameRefCallback);

  const color = watch('color');
  const onChangeColor = (newColor?: string) =>
    setValue<'color'>('color', newColor ?? null);

  const icon = watch<'icon'>('icon');
  const onChangeIcon = (newIcon: IconFormNames) =>
    setValue<'icon'>('icon', newIcon);

  useEffect(() => {
    if (nameRef.current && !isEdit) {
      nameRef.current.focus();
    }
  }, [isEdit, nameRef]);

  return (
    <Styled.Form noValidate onSubmit={submitForm}>
      <FormDialogHeader
        onCancel={onCancel}
        title={t(
          isEdit
            ? 'dialogs.edit-smart-list.title'
            : 'dialogs.create-smart-list.title',
        )}
        canSubmit={isValid}
      />

      <Styled.Body>
        <Styled.Section>
          <Styled.Label>{t('forms.tag.fields.name.label')}</Styled.Label>
          <FormInput
            {...nameProps}
            ref={nameRef}
            placeholder={t('dialogs.create-smart-list.placeholder')}
            error={errors.name?.message}
            autoComplete="off"
          />

          <Styled.Label>{t('general.labels.color')}</Styled.Label>
          <ColorPickerFormSelect
            placeholder={t('general.labels.select')}
            color={color ?? undefined}
            onChange={onChangeColor}
            canClear
          />

          <Styled.Label>{t('forms.smart-list.fields.icon.label')}</Styled.Label>
          <FormIcon
            value={icon ?? IconFormNames.SmartList}
            onChange={onChangeIcon}
          />

          {!!filterEditor && (
            <>
              <Styled.Label>
                {t('forms.smart-list.fields.filters.label')}
              </Styled.Label>
              {filterEditor}
            </>
          )}
        </Styled.Section>
      </Styled.Body>

      <Styled.Footer>
        <Button
          variant={Variants.Contained}
          size={ButtonSizes.Medium}
          color={Colors.White}
          onClick={onCancel}
          type="button"
        >
          {t('general.labels.cancel')}
        </Button>
        <Button size={ButtonSizes.Medium} type="submit" disabled={!isValid}>
          {t('general.labels.save')}
        </Button>
      </Styled.Footer>
    </Styled.Form>
  );
};
